import EntityAttachments from 'components/EntityAttachments';
import EntityLogoEditor from 'components/EntityLogoEditor';
import HmsEntityFormController from 'components/Form/HmsEntityFormController';
import HmsEntityFormGridBasic from 'components/Form/HmsEntityFormGridBasic';
import HmsEntityFormGridFullWidth from 'components/Form/HmsEntityFormGridFullWidth';
import HmsField from 'components/Form/HmsField';
import HmsTabbedTables from 'components/HmsTabbedTables';
import SeasonGroups from 'components/Season/SeasonGroups';
import SeasonSpecialRequests from 'components/Season/SeasonSpecialRequests';
import TabsWithUrlMemory from 'components/TabsWithUrlMemory';
import useEntities from 'hooks/useEntities';
import useLoadEntity from 'hooks/useLoadEntity';
import openInNewTab from 'utils/openInNewTab';

const Season = () => {
  const entities = useEntities();
  const entity = entities.Season;
  const parentEntity = entities.Competition;
  const childEntity = entities.Group;
  const { id, parentId, isLoading, isError, isNotFound, data, reload, parentData, childrenData } = useLoadEntity({ entity, parentEntity, childEntity });
  const { relationsData, reloadRelations } = useLoadEntity({ entity,
    relations: [
      'Attachments',
      'SeasonSpecialRequests',
    ].join(',')
  });

  return (
    <>
      <HmsEntityFormController
        {... {entity, parentEntity, id, parentId, isError, isLoading, isNotFound, data, reload, parentData, childrenData} }
        systemButtons={[
          {
            label: 'Open embed - Terms',
            onClick: () => { openInNewTab(`/embed/season_terms?seasonId=${id}&lang=cs`) },
            disabled: id == '_new_',
          },
        ]}
        form={(values) => (
          <TabsWithUrlMemory
            className="no-tab-padding w-[calc(100vw-75px)] desktop:w-[calc(100vw-280px)]"
            items={[
              {
                label: `Basic`,
                children: <>  
                  <div className="flex flex-col desktop:flex-row w-full">
                    <div className="flex items-center justify-center ml-4 mr-6 mb-5 desktop:mb-0 ">
                      <EntityLogoEditor entity={entities.Season} id={id} data={data} size={112} />
                    </div>
                    <div className="flex-auto">
                    <HmsEntityFormGridBasic>
                      <HmsField name="name" autoFocus />
                      <HmsField name="nick" />
                      <HmsField name="short" />
                      <HmsField name="startDate" />
                      <HmsField name="endDate" />
                      <HmsField name="status" />
                    </HmsEntityFormGridBasic>
                    </div>
                  </div>
                </>
              },
              {
                label: `Team Registration Settings`,
                children: <>
                  <HmsEntityFormGridBasic>
                    <HmsField name="registrationFee" addonAfter="Kč" />
                  </HmsEntityFormGridBasic>
                  <HmsEntityFormGridFullWidth>
                    {/* <TipTapEditor /> */}
                    <HmsField
                      name="terms"
                      type="HTML"
                    />
                  </HmsEntityFormGridFullWidth>
                </>
              },
              {
                label: `Player Registration Settings`,
                children: <>
                  <HmsEntityFormGridBasic>
                    <HmsField
                      name="playerRegMandatory"
                      type="BOOLEAN_SWITCH"
                      label="Use Players Registration"
                    />
                  </HmsEntityFormGridBasic>
                  {/* All below is disabled if playerRegMandatory is not true */}
                  <div className={values?.playerRegMandatory ? '' : 'opacity-50 pointer-events-none'}>
                    <div className="mt-6 mb-2 font-semibold">Terms #1: Confirmation of processing of personal details</div>
                    <HmsEntityFormGridBasic>
                      <HmsField
                        name="usePlayerRegTerms"
                        type="BOOLEAN_YESNO"
                        label="Do you want to use it"
                      />
                    </HmsEntityFormGridBasic>
                    <div className={values?.usePlayerRegTerms ? '' : 'opacity-50 pointer-events-none'}>
                      <HmsEntityFormGridFullWidth>
                        <HmsField
                          name="playerRegTerms"
                          type="HTML"
                          disabled={!values?.usePlayerRegTerms}
                        />
                      </HmsEntityFormGridFullWidth>
                    </div>  

                    <div className="mt-6 mb-2 font-semibold">Terms #2: GDPR agreement</div>
                      <HmsEntityFormGridBasic>
                        <HmsField
                          name="usePlayerRegTerms2"
                          type="BOOLEAN_YESNO"
                          label="Do you want to use it"
                        />
                      </HmsEntityFormGridBasic>
                    <div className={values?.usePlayerRegTerms2 ? '' : 'opacity-50 pointer-events-none'}>

                    <HmsEntityFormGridFullWidth>
                      <HmsField
                        name="playerRegTerms2"
                        type="HTML"
                        disabled={!values?.usePlayerRegTerms2}
                      />
                    </HmsEntityFormGridFullWidth>
                    </div>

                    <div className="mt-6 mb-2 font-semibold">Terms #3: Agreement with the terms and conditions</div>
                    <HmsEntityFormGridBasic>
                      <HmsField
                        name="usePlayerRegTerms3"
                        type="BOOLEAN_YESNO"
                        label="Do you want to use it"
                      />
                    </HmsEntityFormGridBasic>
                    <div className={values?.usePlayerRegTerms3 ? '' : 'opacity-50 pointer-events-none'}>
                    <HmsEntityFormGridFullWidth>
                      <HmsField
                        name="playerRegTerms3"
                        type="HTML"
                        disabled={!values?.usePlayerRegTerms3}
                      />
                    </HmsEntityFormGridFullWidth>
                    </div>

                    <HmsEntityFormGridBasic className="mt-6">
                      <HmsField
                        name="playerRegFeeMandatory"
                        type="BOOLEAN_SWITCH"
                        label="Require Fee"
                      />
                    </HmsEntityFormGridBasic>
                    {/* Fee section disabled if playerRegFeeMandatory is not true */}
                    <div className={values?.playerRegFeeMandatory ? '' : 'opacity-50 pointer-events-none'}>
                      <HmsEntityFormGridBasic>
                        <HmsField name="playerRegFee" addonAfter="Kč" />
                        <HmsField name="playerRegFeeName" />
                        <HmsField name="playerRegFeeDescription" type="LONG_STRING" />
                      </HmsEntityFormGridBasic>
                    </div>
                  </div>
                </>
              },
            ].map(tab => ({ ...tab, key: tab.label }))}
          />
        )}
        footer={id != '_new_' && (
          <div className="mt-4">
            <HmsTabbedTables
              tables={[
                {
                  title: "Groups" + (childrenData ? (' (' + childrenData?.length + ')') : ''),
                  children: <SeasonGroups data={childrenData} sourceEntityId={data?.seasonId} /> 
                },
                {
                  title: "Attachments" + (relationsData?.Attachments ? (' (' + relationsData?.Attachments?.length + ')') : ''),
                  children: <EntityAttachments entity={entity.name} id={data?.[entity.primaryKey]} data={relationsData} reload={reloadRelations} />,
                },
                {
                  title: "Special Requests" + (relationsData?.SeasonSpecialRequests ? (' (' + relationsData?.SeasonSpecialRequests?.length + ')') : ''),
                  children: <SeasonSpecialRequests data={relationsData?.SeasonSpecialRequests} sourceEntityId={data?.seasonId} /> 
                },                
              ]}
            />
          </div>
        )}
      />
    </>
  );
}

export default Season
